import { useEffect, useRef, type ReactNode } from "react";
import { Breadcrumbs, type Crumb } from "./Breadcrumbs";
import { pageTitle } from "../lib/brand";

// The first page view keeps focus where the browser put it; after that, every
// route change moves focus to the new H1 so screen readers announce the page.
// Never out of a field the person is typing in: a page that finishes loading
// (its PageHeader mounts again) would otherwise swallow their Enter. Nor out of
// an open menu (the header's organisation or account menu), which would close it.
let firstPageView = true;
const FIELD_IN_USE = "input, textarea, select, [contenteditable]:not([contenteditable='false']), [aria-expanded='true'], [role='menu'] *";

/**
 * The design system's PageHeader: breadcrumbs above the H1, an optional lead line and
 * actions (top right).
 * Sets document.title to "{title} · Convenor" (the product name from /api/meta;
 * `documentTitle` overrides the first part). Use exactly one per page.
 */
export function PageHeader({
  title,
  lead,
  actions,
  breadcrumbs = [],
  meta,
  documentTitle,
  kicker,
}: {
  title: string;
  documentTitle?: string;
  /** The small uppercase label above the title. */
  kicker?: ReactNode;
  lead?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: Crumb[];
  meta?: ReactNode;
}) {
  const h1 = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    document.title = pageTitle(documentTitle ?? title);
  }, [title, documentTitle]);

  useEffect(() => {
    if (firstPageView) {
      firstPageView = false;
      return;
    }
    if (document.activeElement?.matches(FIELD_IN_USE)) return;
    h1.current?.focus();
  }, []);

  return (
    <div className="page-header">
      <div>
        <Breadcrumbs items={breadcrumbs} />
        {kicker ? <span className="label">{kicker}</span> : null}
        <h1 ref={h1} tabIndex={-1}>
          {title}
        </h1>
        {meta ? <div className="page-header__meta">{meta}</div> : null}
        {lead ? <p className="lead">{lead}</p> : null}
      </div>
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}
